import type { IndexedDbObjectStoreConfig } from './indexed-db-object-store.types';

/**
 * Хранилище записей в одном object store IndexedDB (keyPath).
 *
 * Гарантии:
 * - операция считается выполненной только после `oncomplete` транзакции
 *   (ошибка квоты приходит как abort уже после успешного request);
 * - abort/error транзакции и ошибки запроса отклоняют промис;
 * - неудачное открытие БД не кэшируется — следующий вызов попробует снова.
 */
export class IndexedDbObjectStore<T> {
  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor(private readonly config: IndexedDbObjectStoreConfig) {}

  async delete(key: string): Promise<void> {
    await this.execute('readwrite', store => store.delete(key));
  }

  async entries(): Promise<T[]> {
    return this.execute<T[]>('readonly', store => store.getAll());
  }

  async get(key: string): Promise<T | null> {
    const record = await this.execute<T | undefined>('readonly', store =>
      store.get(key),
    );

    return record ?? null;
  }

  async put(record: T): Promise<void> {
    await this.execute('readwrite', store => store.put(record));
  }

  private open(): Promise<IDBDatabase> {
    if (this.databasePromise) {
      return this.databasePromise;
    }

    const { databaseName, keyPath, storeName, version } = this.config;
    const databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));

        return;
      }

      const request = indexedDB.open(databaseName, version);

      request.onerror = () =>
        reject(request.error ?? new Error(`Failed to open ${databaseName}`));
      request.onblocked = () =>
        reject(new Error(`Opening ${databaseName} is blocked`));
      request.onupgradeneeded = () => {
        const database = request.result;

        if (!database.objectStoreNames.contains(storeName)) {
          database.createObjectStore(storeName, { keyPath });
        }
      };
      request.onsuccess = () => {
        const database = request.result;

        database.onversionchange = () => {
          database.close();
          this.databasePromise = null;
        };
        resolve(database);
      };
    });

    this.databasePromise = databasePromise;
    databasePromise.catch(() => {
      if (this.databasePromise === databasePromise) {
        this.databasePromise = null;
      }
    });

    return databasePromise;
  }

  private async execute<R>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<R>,
  ): Promise<R> {
    const database = await this.open();

    return new Promise<R>((resolve, reject) => {
      let transaction: IDBTransaction;

      try {
        transaction = database.transaction(this.config.storeName, mode);
      } catch (error) {
        // Соединение могло быть закрыто (versionchange) — переоткроем при следующем вызове
        this.databasePromise = null;
        reject(error instanceof Error ? error : new Error(String(error)));

        return;
      }

      const request = operation(transaction.objectStore(this.config.storeName));
      let result: R | undefined;

      request.onsuccess = () => {
        result = request.result;
      };
      request.onerror = () =>
        reject(request.error ?? new Error('IndexedDB request failed'));
      transaction.oncomplete = () => resolve(result as R);
      transaction.onabort = () =>
        reject(
          transaction.error ??
            request.error ??
            new Error('IndexedDB transaction aborted'),
        );
    });
  }
}
